import { baseApi } from './baseApi';

const taskApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── CRUD ──
    listTasks: builder.query({
      query: (params) => ({ url: '/tasks', params }),
      providesTags: (result) =>
        result?.data
          ? [...result.data.map((t) => ({ type: 'Task', id: t._id })), { type: 'Task', id: 'LIST' }]
          : [{ type: 'Task', id: 'LIST' }],
    }),

    getTask: builder.query({
      query: (taskId) => `/tasks/${taskId}`,
      providesTags: (result, error, id) => [{ type: 'Task', id }],
    }),

    createTask: builder.mutation({
      query: (body) => ({ url: '/tasks', method: 'POST', body }),
      invalidatesTags: [{ type: 'Task', id: 'LIST' }, { type: 'Project' }],
    }),

    updateTask: builder.mutation({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}`, method: 'PUT', body }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'Task', id: taskId }, { type: 'Task', id: 'LIST' }, { type: 'Project' },
      ],
    }),

    deleteTask: builder.mutation({
      query: (taskId) => ({ url: `/tasks/${taskId}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Task', id: 'LIST' }, { type: 'Project' }],
    }),

    // ── Comments ──
    addComment: builder.mutation({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/comments`, method: 'POST', body }),
      invalidatesTags: (result, error, { taskId }) => [{ type: 'Task', id: taskId }],
    }),

    updateComment: builder.mutation({
      query: ({ taskId, commentId, content }) => ({
        url: `/tasks/${taskId}/comments/${commentId}`, method: 'PUT', body: { content },
      }),
      invalidatesTags: (result, error, { taskId }) => [{ type: 'Task', id: taskId }],
    }),

    deleteComment: builder.mutation({
      query: ({ taskId, commentId }) => ({
        url: `/tasks/${taskId}/comments/${commentId}`, method: 'DELETE',
      }),
      invalidatesTags: (result, error, { taskId }) => [{ type: 'Task', id: taskId }],
    }),

    // ── Dependencies ──
    addDependency: builder.mutation({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/dependencies`, method: 'POST', body }),
      invalidatesTags: (result, error, { taskId }) => [{ type: 'Task', id: taskId }],
    }),

    removeDependency: builder.mutation({
      query: ({ taskId, depTaskId }) => ({
        url: `/tasks/${taskId}/dependencies/${depTaskId}`, method: 'DELETE',
      }),
      invalidatesTags: (result, error, { taskId }) => [{ type: 'Task', id: taskId }],
    }),

    // ── Time logging ──
    logTime: builder.mutation({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/time`, method: 'POST', body }),
      invalidatesTags: (result, error, { taskId }) => [{ type: 'Task', id: taskId }],
    }),

    // ── Board & Reorder ──
    getBoardTasks: builder.query({
      query: (params) => ({ url: '/tasks/board', params }),
      providesTags: [{ type: 'Task', id: 'BOARD' }],
    }),

    // The project's status registry — what every status control may offer.
    // Omit projectId for cross-project surfaces; the server returns the
    // workspace-wide union. Invalidated by 'Project' because editing a
    // project's columns or customStatuses changes the answer.
    getTaskStatuses: builder.query({
      query: (params = {}) => ({ url: '/tasks/statuses', params }),
      providesTags: (result, error, arg) => [
        { type: 'TaskStatuses', id: arg?.projectId || 'WORKSPACE' },
        { type: 'Project' },
      ],
    }),

    reorderTask: builder.mutation({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/reorder`, method: 'PATCH', body }),
      invalidatesTags: [{ type: 'Task', id: 'BOARD' }, { type: 'Task', id: 'LIST' }, 'TaskStats', { type: 'Project' }],
    }),

    // ── Bulk & Stats ──
    bulkUpdateTasks: builder.mutation({
      query: (body) => ({ url: '/tasks/bulk', method: 'POST', body }),
      invalidatesTags: [{ type: 'Task', id: 'LIST' }, { type: 'Task', id: 'BOARD' }, 'TaskStats'],
    }),

    bulkDeleteTasks: builder.mutation({
      query: (body) => ({ url: '/tasks/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Task', id: 'LIST' }, { type: 'Task', id: 'BOARD' }, 'TaskStats', { type: 'Project' }],
    }),

    getTaskStats: builder.query({
      query: (params) => ({ url: '/tasks/stats', params }),
      providesTags: ['TaskStats'],
    }),

    // ── Clone / Duplicate ──
    cloneTask: builder.mutation({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/clone`, method: 'POST', body }),
      invalidatesTags: [{ type: 'Task', id: 'LIST' }, { type: 'Task', id: 'BOARD' }, 'TaskStats', { type: 'Project' }],
    }),

    // ── Task Templates ──
    listTaskTemplates: builder.query({
      query: (params) => ({ url: '/tasks/templates', params }),
      providesTags: (result) =>
        result?.data
          ? [...result.data.map((t) => ({ type: 'TaskTemplate', id: t._id })), { type: 'TaskTemplate', id: 'LIST' }]
          : [{ type: 'TaskTemplate', id: 'LIST' }],
    }),

    getTaskTemplate: builder.query({
      query: (templateId) => `/tasks/templates/${templateId}`,
      providesTags: (result, error, id) => [{ type: 'TaskTemplate', id }],
    }),

    createTaskTemplate: builder.mutation({
      query: (body) => ({ url: '/tasks/templates', method: 'POST', body }),
      invalidatesTags: [{ type: 'TaskTemplate', id: 'LIST' }],
    }),

    createTemplateFromTask: builder.mutation({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/to-template`, method: 'POST', body }),
      invalidatesTags: [{ type: 'TaskTemplate', id: 'LIST' }],
    }),

    deleteTaskTemplate: builder.mutation({
      query: (templateId) => ({ url: `/tasks/templates/${templateId}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'TaskTemplate', id: 'LIST' }],
    }),

    createTaskFromTemplate: builder.mutation({
      query: ({ templateId, ...body }) => ({ url: `/tasks/templates/${templateId}/use`, method: 'POST', body }),
      invalidatesTags: [{ type: 'Task', id: 'LIST' }, { type: 'Task', id: 'BOARD' }, 'TaskStats', { type: 'Project' }],
    }),

    // ── Saved Views ──
    listSavedViews: builder.query({
      query: (params) => ({ url: '/tasks/views/saved', params }),
      providesTags: (result) =>
        result?.data
          ? [...result.data.map((v) => ({ type: 'SavedView', id: v._id })), { type: 'SavedView', id: 'LIST' }]
          : [{ type: 'SavedView', id: 'LIST' }],
    }),

    createSavedView: builder.mutation({
      query: (body) => ({ url: '/tasks/views/saved', method: 'POST', body }),
      invalidatesTags: [{ type: 'SavedView', id: 'LIST' }],
    }),

    updateSavedView: builder.mutation({
      query: ({ viewId, ...body }) => ({ url: `/tasks/views/saved/${viewId}`, method: 'PUT', body }),
      invalidatesTags: (result, error, { viewId }) => [
        { type: 'SavedView', id: viewId }, { type: 'SavedView', id: 'LIST' },
      ],
    }),

    deleteSavedView: builder.mutation({
      query: (viewId) => ({ url: `/tasks/views/saved/${viewId}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'SavedView', id: 'LIST' }],
    }),

    // ── Gantt ──
    getGanttData: builder.query({
      query: (params) => ({ url: '/tasks/gantt', params }),
      providesTags: [{ type: 'Task', id: 'GANTT' }],
    }),
    cascadeReschedule: builder.mutation({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/reschedule`, method: 'POST', body }),
      invalidatesTags: [{ type: 'Task', id: 'GANTT' }, { type: 'Task', id: 'LIST' }],
    }),

    // ── Attachments ──
    uploadAttachments: builder.mutation({
      query: ({ taskId, formData }) => ({
        url: `/tasks/${taskId}/attachments`,
        method: 'POST',
        body: formData,
        formData: true,
      }),
      invalidatesTags: (result, error, { taskId }) => [{ type: 'Task', id: taskId }],
    }),
    addExternalAttachment: builder.mutation({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/attachments/external`, method: 'POST', body }),
      invalidatesTags: (r, e, { taskId }) => [{ type: 'Task', id: taskId }],
    }),
    removeAttachment: builder.mutation({
      query: ({ taskId, attachmentId }) => ({
        url: `/tasks/${taskId}/attachments/${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { taskId }) => [{ type: 'Task', id: taskId }],
    }),

    // ── Watchers ──
    addWatcher: builder.mutation({
      query: ({ taskId, userId }) => ({
        url: `/tasks/${taskId}/watchers`,
        method: 'POST',
        body: { userId },
      }),
      invalidatesTags: (result, error, { taskId }) => [{ type: 'Task', id: taskId }],
    }),
    removeWatcher: builder.mutation({
      query: ({ taskId, userId }) => ({
        url: `/tasks/${taskId}/watchers/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { taskId }) => [{ type: 'Task', id: taskId }],
    }),

    // ── Gantt Baseline ──
    saveBaseline: builder.mutation({
      query: (projectId) => ({
        url: `/tasks/gantt/baseline?projectId=${projectId}`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Task', id: 'GANTT' }],
    }),
    clearBaseline: builder.mutation({
      query: (projectId) => ({
        url: `/tasks/gantt/baseline?projectId=${projectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Task', id: 'GANTT' }],
    }),

    // ── Checklist ──
    addChecklistItem: builder.mutation({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/checklist`, method: 'POST', body }),
      invalidatesTags: (r, e, { taskId }) => [{ type: 'Task', id: taskId }],
    }),
    updateChecklistItem: builder.mutation({
      query: ({ taskId, itemId, ...body }) => ({ url: `/tasks/${taskId}/checklist/${itemId}`, method: 'PUT', body }),
      invalidatesTags: (r, e, { taskId }) => [{ type: 'Task', id: taskId }],
    }),
    toggleChecklistItem: builder.mutation({
      query: ({ taskId, itemId }) => ({ url: `/tasks/${taskId}/checklist/${itemId}/toggle`, method: 'PATCH' }),
      invalidatesTags: (r, e, { taskId }) => [{ type: 'Task', id: taskId }],
    }),
    removeChecklistItem: builder.mutation({
      query: ({ taskId, itemId }) => ({ url: `/tasks/${taskId}/checklist/${itemId}`, method: 'DELETE' }),
      invalidatesTags: (r, e, { taskId }) => [{ type: 'Task', id: taskId }],
    }),
    reorderChecklist: builder.mutation({
      query: ({ taskId, itemIds }) => ({ url: `/tasks/${taskId}/checklist/reorder`, method: 'PATCH', body: { itemIds } }),
      invalidatesTags: (r, e, { taskId }) => [{ type: 'Task', id: taskId }],
    }),

    // ── Recurring ──
    setRecurring: builder.mutation({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/recurring`, method: 'POST', body }),
      invalidatesTags: (r, e, { taskId }) => [{ type: 'Task', id: taskId }],
    }),
    pauseRecurring: builder.mutation({
      query: (taskId) => ({ url: `/tasks/${taskId}/recurring/pause`, method: 'PATCH' }),
      invalidatesTags: (r, e, taskId) => [{ type: 'Task', id: taskId }],
    }),
    disableRecurring: builder.mutation({
      query: (taskId) => ({ url: `/tasks/${taskId}/recurring`, method: 'DELETE' }),
      invalidatesTags: (r, e, taskId) => [{ type: 'Task', id: taskId }],
    }),
    skipNextOccurrence: builder.mutation({
      query: (taskId) => ({ url: `/tasks/${taskId}/recurring/skip`, method: 'POST' }),
      invalidatesTags: (r, e, taskId) => [{ type: 'Task', id: taskId }],
    }),

    // ── Calendar (Step 16) ──
    getCalendarEvents: builder.query({
      query: (params) => ({ url: '/tasks/calendar', params }),
      providesTags: ['Task', 'Milestone', 'Sprint'],
    }),

    // ═══ 3.1 DEPENDENCY GRAPH ═══
    getDependencyGraph: builder.query({
      query: (params) => ({ url: '/tasks/graph/dependencies', params }),
      providesTags: ['TaskGraph'],
    }),
    whatIfSimulation: builder.mutation({
      query: (body) => ({ url: '/tasks/graph/what-if', method: 'POST', body }),
    }),
    getDependencyImpact: builder.query({
      query: ({ taskId, slipDays }) => ({ url: `/tasks/graph/impact/${taskId}`, params: { slipDays } }),
    }),

    // ═══ 3.2 BULK OPS ═══
    // A bulk operation can change status, assignment, sprint, project and
    // milestone at once. Invalidating only 'Task' left the stats panels, the
    // project progress bar and the board counts showing pre-action values —
    // which reads as "the action did nothing".
    bulkOperations: builder.mutation({
      query: (body) => ({ url: '/tasks/bulk/operations', method: 'POST', body }),
      invalidatesTags: [
        'Task', { type: 'Task', id: 'LIST' }, { type: 'Task', id: 'BOARD' },
        'TaskStats', { type: 'Project' }, 'Sprint', 'Milestone',
      ],
    }),
    bulkImportTasks: builder.mutation({
      query: (body) => ({ url: '/tasks/bulk/import', method: 'POST', body }),
      invalidatesTags: [
        'Task', { type: 'Task', id: 'LIST' }, { type: 'Task', id: 'BOARD' },
        'TaskStats', { type: 'Project' },
      ],
    }),

    // ═══ 3.4 APPROVALS ═══
    requestApproval: builder.mutation({
      query: (body) => ({ url: '/tasks/approvals', method: 'POST', body }),
      invalidatesTags: ['TaskApproval'],
    }),
    listApprovals: builder.query({
      query: (params) => ({ url: '/tasks/approvals', params }),
      providesTags: ['TaskApproval'],
    }),
    processApproval: builder.mutation({
      query: ({ approvalId, ...body }) => ({ url: `/tasks/approvals/${approvalId}`, method: 'PUT', body }),
      invalidatesTags: ['TaskApproval', 'Task'],
    }),

    // ═══ 3.5 VIEWS ═══
    getTimelineData: builder.query({
      query: (params) => ({ url: '/tasks/views/timeline', params }),
      providesTags: ['Task'],
    }),
    getWorkloadData: builder.query({
      query: (params) => ({ url: '/tasks/views/workload', params }),
      providesTags: ['Task'],
    }),

    // ═══ 3.6 ADVANCED SEARCH ═══
    advancedSearch: builder.mutation({
      query: (body) => ({ url: '/tasks/search/advanced', method: 'POST', body }),
    }),

    // ═══ 3.7 RELATIONS ═══
    createRelation: builder.mutation({
      query: (body) => ({ url: '/tasks/relations', method: 'POST', body }),
      invalidatesTags: ['TaskRelation'],
    }),
    listTaskRelations: builder.query({
      query: (taskId) => `/tasks/${taskId}/relations`,
      providesTags: ['TaskRelation'],
    }),
    deleteRelation: builder.mutation({
      query: (relationId) => ({ url: `/tasks/relations/${relationId}`, method: 'DELETE' }),
      invalidatesTags: ['TaskRelation'],
    }),

    // ═══ 3.8 PLANNING POKER ═══
    createPokerSession: builder.mutation({
      query: (body) => ({ url: '/tasks/poker', method: 'POST', body }),
      invalidatesTags: ['PlanningPoker'],
    }),
    submitPokerVote: builder.mutation({
      query: ({ sessionId, value }) => ({ url: `/tasks/poker/${sessionId}/vote`, method: 'PUT', body: { value } }),
      invalidatesTags: ['PlanningPoker'],
    }),
    revealPokerVotes: builder.mutation({
      query: (sessionId) => ({ url: `/tasks/poker/${sessionId}/reveal`, method: 'PUT' }),
      invalidatesTags: ['PlanningPoker'],
    }),
    setPokerConsensus: builder.mutation({
      query: ({ sessionId, consensusValue }) => ({ url: `/tasks/poker/${sessionId}/consensus`, method: 'PUT', body: { consensusValue } }),
      invalidatesTags: ['PlanningPoker', 'Task'],
    }),
    getEstimationAccuracy: builder.query({
      query: (params) => ({ url: '/tasks/estimation/accuracy', params }),
    }),
    getRollupEstimates: builder.query({
      query: (taskId) => `/tasks/${taskId}/rollup`,
    }),

    // ═══ 3.9 MERGE & SPLIT ═══
    mergeTasks: builder.mutation({
      query: (body) => ({ url: '/tasks/merge', method: 'POST', body }),
      invalidatesTags: ['Task'],
    }),
    splitTask: builder.mutation({
      query: ({ taskId, splitItems }) => ({ url: `/tasks/${taskId}/split`, method: 'POST', body: { splitItems } }),
      invalidatesTags: ['Task'],
    }),

    // ═══ 3.10 AUTOMATION ═══
    listAutomationRules: builder.query({
      query: (params) => ({ url: '/tasks/automation', params }),
      providesTags: ['TaskAutomation'],
    }),
    createAutomationRule: builder.mutation({
      query: (body) => ({ url: '/tasks/automation', method: 'POST', body }),
      invalidatesTags: ['TaskAutomation'],
    }),
    updateAutomationRule: builder.mutation({
      query: ({ ruleId, ...body }) => ({ url: `/tasks/automation/${ruleId}`, method: 'PUT', body }),
      invalidatesTags: ['TaskAutomation'],
    }),
    deleteAutomationRule: builder.mutation({
      query: (ruleId) => ({ url: `/tasks/automation/${ruleId}`, method: 'DELETE' }),
      invalidatesTags: ['TaskAutomation'],
    }),

    // ═══ 3.11 REACTIONS & PIN ═══
    addReaction: builder.mutation({
      query: ({ taskId, commentId, emoji }) => ({ url: `/tasks/${taskId}/comments/${commentId}/reactions`, method: 'POST', body: { emoji } }),
      invalidatesTags: ['TaskReaction'],
    }),
    removeReaction: builder.mutation({
      query: ({ taskId, commentId, emoji }) => ({ url: `/tasks/${taskId}/comments/${commentId}/reactions`, method: 'DELETE', body: { emoji } }),
      invalidatesTags: ['TaskReaction'],
    }),
    getReactions: builder.query({
      query: (taskId) => `/tasks/${taskId}/reactions`,
      providesTags: ['TaskReaction'],
    }),
    pinComment: builder.mutation({
      query: ({ taskId, commentId, pin }) => ({ url: `/tasks/${taskId}/comments/${commentId}/pin`, method: 'PUT', body: { pin } }),
      invalidatesTags: ['Task'],
    }),

    // ═══ 3.12 TIME WITH CATEGORY ═══
    logTimeWithCategory: builder.mutation({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/time-category`, method: 'POST', body }),
      invalidatesTags: ['Task', 'TimeLog'],
    }),
  }),
});

export const {
  useListTasksQuery, useGetTaskQuery, useCreateTaskMutation, useUpdateTaskMutation,
  useDeleteTaskMutation, useAddCommentMutation, useUpdateCommentMutation,
  useDeleteCommentMutation, useAddDependencyMutation, useRemoveDependencyMutation,
  useLogTimeMutation, useGetBoardTasksQuery, useReorderTaskMutation,
  useBulkUpdateTasksMutation, useBulkDeleteTasksMutation, useGetTaskStatsQuery,
  useCloneTaskMutation,
  useListTaskTemplatesQuery, useGetTaskTemplateQuery,
  useCreateTaskTemplateMutation, useCreateTemplateFromTaskMutation,
  useDeleteTaskTemplateMutation, useCreateTaskFromTemplateMutation,
  useListSavedViewsQuery, useCreateSavedViewMutation, useUpdateSavedViewMutation,
  useDeleteSavedViewMutation,
  useGetGanttDataQuery, useCascadeRescheduleMutation,
  useUploadAttachmentsMutation, useRemoveAttachmentMutation, useAddExternalAttachmentMutation,
  useAddWatcherMutation, useRemoveWatcherMutation,
  useSaveBaselineMutation, useClearBaselineMutation,
  useGetCalendarEventsQuery,
  useAddChecklistItemMutation, useUpdateChecklistItemMutation,
  useToggleChecklistItemMutation, useRemoveChecklistItemMutation,
  useReorderChecklistMutation,
  useSetRecurringMutation, usePauseRecurringMutation,
  useDisableRecurringMutation, useSkipNextOccurrenceMutation,
  useGetTaskStatusesQuery, useLazyGetTaskStatusesQuery,
  // 3.1 Dependency Graph
  useGetDependencyGraphQuery, useWhatIfSimulationMutation, useGetDependencyImpactQuery,
  // 3.2 Bulk
  useBulkOperationsMutation, useBulkImportTasksMutation,
  // 3.4 Approvals
  useRequestApprovalMutation, useListApprovalsQuery, useProcessApprovalMutation,
  // 3.5 Views
  useGetTimelineDataQuery, useGetWorkloadDataQuery,
  // 3.6 Search
  useAdvancedSearchMutation,
  // 3.7 Relations
  useCreateRelationMutation, useListTaskRelationsQuery, useDeleteRelationMutation,
  // 3.8 Poker
  useCreatePokerSessionMutation, useSubmitPokerVoteMutation, useRevealPokerVotesMutation,
  useSetPokerConsensusMutation, useGetEstimationAccuracyQuery, useGetRollupEstimatesQuery,
  // 3.9 Merge/Split
  useMergeTasksMutation, useSplitTaskMutation,
  // 3.10 Automation
  useListAutomationRulesQuery, useCreateAutomationRuleMutation,
  useUpdateAutomationRuleMutation, useDeleteAutomationRuleMutation,
  // 3.11 Reactions
  useAddReactionMutation, useRemoveReactionMutation, useGetReactionsQuery, usePinCommentMutation,
  // 3.12 Time
  useLogTimeWithCategoryMutation,
} = taskApi;